import { z } from 'zod';
import { Timestamp } from './common.js';
import { DEFAULT_ORG_RESEARCH_DEPTH } from './org-settings.js';

/**
 * What a charge prices (credits launch spec, steps 1–4). Each but the event scan
 * and the import has its own versioned catalog rate on the server; a quick look is
 * a person's quick search or an organization lookup, and within the plan's monthly
 * allowance costs nothing. An event scan is priced per attendee and an import per
 * row beyond the month's included rows ({@link CREDIT_RATE_KEYS}).
 */
export const FIXED_PRICE_OPERATIONS = [
  'quick_look',
  'person_research',
  'research_recheck',
  'org_investigation',
  'event_research',
  'travel_brief',
] as const;
export type FixedPriceOperation = (typeof FIXED_PRICE_OPERATIONS)[number];
export const CREDIT_OPERATIONS = [...FIXED_PRICE_OPERATIONS, 'event_scan', 'import'] as const;
export const CreditOperation = z.enum(CREDIT_OPERATIONS);
export type CreditOperation = z.infer<typeof CreditOperation>;

/**
 * Every catalog rate an operator can publish: one per fixed-price operation,
 * the event scan's two per-attendee rates (an attendee the scan searches on
 * the web, and one it rates only from the record), and the import's rate per
 * 10 rows beyond the month's included rows.
 */
export const CREDIT_RATE_KEYS = [
  ...FIXED_PRICE_OPERATIONS,
  'event_attendee',
  'event_attendee_on_record',
  'import_rows',
] as const;

/** `import_rows` is priced per this many rows. */
export const IMPORT_ROWS_PER_RATE = 10;
export const CreditRateKey = z.enum(CREDIT_RATE_KEYS);
export type CreditRateKey = z.infer<typeof CreditRateKey>;

/**
 * Whether a research run's config (the office's body, or the materialized row)
 * is a Refresh: `recheck_since` on a body that plans nothing, or every planned
 * leg an `update`.
 */
export function isRecheckConfig(config: Record<string, unknown> | null | undefined): boolean {
  const c = config ?? {};
  const initial = c['initial'];
  return (
    (typeof c['recheck_since'] === 'string' && initial === undefined) ||
    (Array.isArray(initial) &&
      initial.length > 0 &&
      initial.every((leg) => (leg as { mode?: unknown } | null)?.mode === 'update'))
  );
}

/**
 * **What a run costs, by what it is**, or null when it is free. One rule for
 * the press and the server's admission, so a button never shows a price the
 * server would not charge. A Resume is priced at the run's first admission.
 *
 * - A quick search that continues into research is priced as that research, and
 *   is not a quick look.
 * - A research run whose every leg is an update is the re-check; any other
 *   research run, at any depth, is person research.
 * - An organization lookup is a quick look; an investigation is its own price.
 * - Only the travel brief's collecting runs are priced; a revise, and every other
 *   report type, is free. So are brief rewrites.
 * - An event scan is priced per attendee at admission.
 */
export function creditOperationFor(
  kind: string,
  config: Record<string, unknown> | null | undefined,
): CreditOperation | null {
  const c = config ?? {};
  switch (kind) {
    case 'quick_search':
      return c['auto_continue'] === 'research' ? 'person_research' : 'quick_look';
    case 'research':
      return isRecheckConfig(c) ? 'research_recheck' : 'person_research';
    case 'org_lookup':
      // Absent depth is the server's default, exactly as run creation resolves it.
      return (c['depth'] ?? DEFAULT_ORG_RESEARCH_DEPTH) === 'investigation'
        ? 'org_investigation'
        : 'quick_look';
    case 'event_research':
      return 'event_research';
    case 'event_scan':
      return 'event_scan';
    case 'report_generate':
    case 'report_update':
    case 'report_refresh':
      return c['type'] === 'travel_brief' ? 'travel_brief' : null;
    default:
      return null;
  }
}

/**
 * Why a charge closed the way it did. `delivered` is the only outcome that
 * consumes credit or an allowance slot: a completion whose delivery receipt
 * shows what that kind of run is charged for. Every other outcome returns the
 * hold or the slot. `no_confirmed_match` is a quick search's; `not_delivered`
 * any other completion's; `continuation_not_started` a quick search whose
 * research never began; `undone` an import the office took back, which returns
 * its charge even after it settled.
 */
export const CREDIT_CHARGE_OUTCOMES = [
  'delivered',
  'no_confirmed_match',
  'not_delivered',
  'continuation_not_started',
  'failed',
  'cancelled',
  'identity_withdrawn',
  'run_removed',
  'released_by_operator',
  'undone',
] as const;
export const CreditChargeOutcome = z.enum(CREDIT_CHARGE_OUTCOMES);
export type CreditChargeOutcome = z.infer<typeof CreditChargeOutcome>;

export const CreditChargeRow = z
  .object({
    id: z.uuid(),
    /** The run it priced; null for an import's charge, which names `import_id`. */
    run_id: z.uuid().nullable(),
    import_id: z.uuid().nullable(),
    operation: CreditOperation,
    /** Null for a report run and an import; `subject_name` is then the report's
     * title or the import's description. */
    subject_entity_id: z.uuid().nullable(),
    subject_name: z.string().nullable(),
    created_at: Timestamp,
    state: z.enum(['open', 'charged', 'returned']),
    basis: z.enum(['allowance', 'credit']),
    units: z.number().int().min(0),
    outcome: CreditChargeOutcome.nullable(),
  })
  .strict();
export type CreditChargeRow = z.infer<typeof CreditChargeRow>;

/**
 * `GET /orgs/:orgId/credits` — the office's view of its own credit. An account
 * that is not enrolled in live charging reads `{ charging_enabled: false }` and
 * nothing else, so its pages render exactly as before.
 *
 * Amounts are ledger units (`units_per_credit` to a credit). `next_quick_look_units`
 * is the price of the next quick search: 0 within the monthly allowance, the
 * catalog rate beyond it, and null when the plan starts no paid work. `prices`
 * holds the next price of every operation on the same terms (its `quick_look`
 * is `next_quick_look_units`).
 */
const Price = z.number().int().min(0).nullable();

/**
 * `GET /orgs/:orgId/credits/events/:eventId/scan` — the most a scan started now
 * would cost, for the scan button: each attendee it would search on the web at
 * one rate, each it would rate only from the record at the other. The scan
 * settles only the attendees it screened. `units` is null when the account is
 * not enrolled, and when its plan starts no paid work.
 */
export const EventScanCreditPrice = z
  .object({
    units: Price,
    attendees: z.number().int().min(0),
    attendees_on_record: z.number().int().min(0),
  })
  .strict();
export type EventScanCreditPrice = z.infer<typeof EventScanCreditPrice>;

/**
 * `GET /orgs/:orgId/credits/runs/:runId` — the price the office's Resume of a
 * failed run is admitted at, for its button. `operation` is null when a resume is
 * free: the account is not enrolled, the kind is unpriced, or the run was never
 * admitted under charging. On its own route rather than on the run read, so the
 * run's own responses carry nothing about credit.
 */
export const RunCreditPrice = z
  .object({ operation: CreditOperation.nullable(), units: Price })
  .strict();
export type RunCreditPrice = z.infer<typeof RunCreditPrice>;

/**
 * `GET /orgs/:orgId/credits/imports/:importId` — the most committing this import
 * now would cost, for its button: its rows (every row not set aside, skipped or
 * folded), less any its Retry source already paid for, beyond what is left of the
 * month's included rows, at the catalog rate per {@link IMPORT_ROWS_PER_RATE}.
 * The import settles only the rows it commits. `units` is null when the account is
 * not enrolled, and when its plan starts no paid work.
 */
export const ImportCreditPrice = z
  .object({
    units: Price,
    /** The rows committing would write. */
    rows: z.number().int().min(0),
    /** Of those, the rows the price does not charge for: the month's included rows
     * left, and any the Retry source paid for. */
    rows_included: z.number().int().min(0),
  })
  .strict();
export type ImportCreditPrice = z.infer<typeof ImportCreditPrice>;

const MonthlyUse = z
  .object({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    used: z.number().int().min(0),
    included: z.number().int().min(0),
  })
  .strict();

export const CreditSummary = z.discriminatedUnion('charging_enabled', [
  z.object({ charging_enabled: z.literal(false) }).strict(),
  z
    .object({
      charging_enabled: z.literal(true),
      units_per_credit: z.number().int().positive(),
      plan: z.object({ key: z.string(), label: z.string(), paid_work: z.boolean() }).strict(),
      balance: z
        .object({ available: z.number().int().min(0), held: z.number().int().min(0) })
        .strict(),
      quick_looks: MonthlyUse,
      /** Import rows this month: committed, or held by an import still writing. */
      import_rows: MonthlyUse,
      /**
       * This month's plan credits: what the plan granted, what is left of it, and
       * when the rest lapses (use-it-or-lose-it; bought credits do not lapse).
       * Null when the plan grants none or none has been granted this month.
       */
      monthly_credits: z
        .object({
          granted: z.number().int().min(0),
          available: z.number().int().min(0),
          expires_at: Timestamp,
        })
        .strict()
        .nullable(),
      next_quick_look_units: z.number().int().min(0).nullable(),
      prices: z
        .object({
          quick_look: Price,
          person_research: Price,
          research_recheck: Price,
          org_investigation: Price,
          event_research: Price,
          travel_brief: Price,
        })
        .strict(),
      recent: z.array(CreditChargeRow).max(20),
    })
    .strict(),
]);
export type CreditSummary = z.infer<typeof CreditSummary>;
